import React, { useMemo, useState } from "react";
import { useNavigate, useLocation, Outlet, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Plus, Home, Car, ClipboardList, ClipboardCheck, Wrench, ShieldAlert,
  FileClock, CalendarClock, Inbox, AlertTriangle, ChevronRight,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { Panel, Spinner, Tag, Input, Select, EmptyState, Btn, ModalShell } from "@/components/kit";
import { NewRequestModal } from "@/components/NewRequestModal";
import { FleetShell } from "@/components/shells";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, formatReg, normalizeReg, REQUEST_LABEL, REQUEST_TYPES } from "@/lib/vda";

// ---------------------------------------------------------------------------
// Canonical data load (single source of truth, loaded once by the layout)
// ---------------------------------------------------------------------------
async function loadExternal() {
  const [{ data: customers }, { data: vehicles }, { data: bookings }] = await Promise.all([
    supabase.from("customers").select("id, name, customer_code, hire_policy, lifecycle_status"),
    supabase.from("vehicles").select("id, registration, make, model, mot_due_date, service_due_date, current_mileage, vehicle_status, active, customer_id, contract_end_date, open_recall, recall_summary"),
    supabase.from("v_external_bookings").select("*").order("created_at", { ascending: false }),
  ]);
  return { customers: customers || [], vehicles: vehicles || [], bookings: bookings || [] };
}

function within90(d) { if (!d) return false; const days = (new Date(d) - new Date()) / 86400000; return days >= -1 && days <= 90; }
const FLEET_ACTIONS = [
  { key: "service", label: "Service due \u226490d", test: (v) => within90(v.service_due_date) },
  { key: "mot", label: "MOT due \u226490d", test: (v) => within90(v.mot_due_date) },
  { key: "recall", label: "Open recalls", test: (v) => !!v.open_recall },
  { key: "contract", label: "Contract ending \u226490d", test: (v) => within90(v.contract_end_date) },
];

// Config-driven navigation: core destinations always present; operational
// destinations appear only when the customer's dashboard config enables that module.
const NAV_DEFS = [
  { key: "dashboard", to: "/fleet", label: "Dashboard", icon: Home, always: true, match: (p) => p === "/fleet" },
  { key: "urgent", to: "/fleet/urgent", label: "Urgent", icon: AlertTriangle, always: true, tone: "danger", match: (p) => p.startsWith("/fleet/urgent") },
  { key: "vehicles", to: "/fleet/vehicles", label: "Vehicles", icon: Car, always: true, match: (p) => p.startsWith("/fleet/vehicle") },
  { key: "requests", to: "/fleet/requests", label: "Requests", icon: ClipboardList, always: true, match: (p) => p.startsWith("/fleet/requests") },
  { key: "checks", to: "/fleet/checks", label: "Weekly Checks", icon: ClipboardCheck, module: "weekly_checks", match: (p) => p.startsWith("/fleet/checks") },
  { key: "service-mot", to: "/fleet/service-mot", label: "Service & MOT", icon: Wrench, module: "forward_service_mot", match: (p) => p.startsWith("/fleet/service-mot") },
  { key: "compliance", to: "/fleet/compliance", label: "Compliance", icon: FileClock, module: "compliance_expiry", match: (p) => p.startsWith("/fleet/compliance") },
  { key: "recalls", to: "/fleet/recalls", label: "Recalls", icon: ShieldAlert, module: "open_recalls", match: (p) => p.startsWith("/fleet/recalls") },
  { key: "contracts", to: "/fleet/contracts", label: "Contracts", icon: CalendarClock, module: "contract_ending", match: (p) => p.startsWith("/fleet/contracts") },
];

// ---------------------------------------------------------------------------
// Layout — loads data once, renders the config-driven shell, feeds child views
// ---------------------------------------------------------------------------
export default function FleetLayout() {
  const nav = useNavigate();
  const { data, loading, error, reload } = useAsync(async () => {
    const [ext, dash] = await Promise.all([loadExternal(), api.fmDashboard()]);
    return { ext, dash };
  }, []);
  const [showPicker, setShowPicker] = useState(false);
  const [pickReg, setPickReg] = useState("");
  const [newVehicle, setNewVehicle] = useState(null);

  if (loading) return <div className="min-h-screen grid place-items-center"><Spinner /></div>;
  if (error || !data) return <FleetLoadError onRetry={reload} />;

  const { ext, dash } = data;
  const customer = ext.customers[0] || {};
  const org = dash?.customer?.name || customer.name || "Your fleet";
  const enabled = dash?.config?.modules || [];
  const navItems = NAV_DEFS.filter((d) => d.always || enabled.includes(d.module));

  const vehByReg = {}; ext.vehicles.forEach((v) => { vehByReg[normalizeReg(v.registration)] = v; });
  const openVehicle = (reg) => { const v = vehByReg[normalizeReg(reg || "")]; if (v) nav(`/fleet/vehicle/${v.id}`); };
  const activeVehicles = ext.vehicles.filter((v) => v.active);

  const ctx = { customer, org, dash, vehicles: ext.vehicles, activeVehicles, bookings: ext.bookings, openVehicle, reload };

  const newRequestBtn = (
    <Btn data-testid="fm-new-request" onClick={() => setShowPicker(true)}><Plus className="h-4 w-4" /> New request</Btn>
  );

  return (
    <FleetShell org={org} nav={navItems} headerRight={newRequestBtn}>
      <Outlet context={ctx} />

      {showPicker && (
        <ModalShell onClose={() => setShowPicker(false)} className="p-5 max-w-md" testId="fm-vehicle-picker-shell">
          <div className="flex flex-col max-h-[80vh]" data-testid="fm-vehicle-picker">
            <h3 className="font-semibold mb-1">New request — select a fleet vehicle</h3>
            <p className="text-xs text-muted-foreground mb-3">Choose any vehicle in your fleet. The request is created on that vehicle's behalf.</p>
            <Input autoFocus data-testid="fm-picker-search" value={pickReg} onChange={(e) => setPickReg(e.target.value)} placeholder="Search registration or make/model" />
            <div className="mt-3 flex-1 min-h-0 overflow-auto divide-y divide-border border border-border rounded-[8px]">
              {activeVehicles
                .filter((v) => { const s = pickReg.trim().toLowerCase(); return !s || `${v.registration} ${v.make} ${v.model}`.toLowerCase().includes(s); })
                .slice(0, 40)
                .map((v) => (
                  <button key={v.id} type="button" data-testid={`fm-pick-${formatReg(v.registration)}`}
                    onClick={() => { setNewVehicle(v); setShowPicker(false); setPickReg(""); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-secondary/40 flex items-center justify-between">
                    <span className="vda-reg font-bold text-primary">{formatReg(v.registration)}</span>
                    <span className="text-xs text-muted-foreground">{v.make} {v.model}</span>
                  </button>
                ))}
            </div>
            <div className="flex justify-end mt-3"><Btn variant="outline" onClick={() => setShowPicker(false)}>Cancel</Btn></div>
          </div>
        </ModalShell>
      )}

      {newVehicle && (
        <NewRequestModal
          vehicle={newVehicle}
          hirePolicy={customer.hire_policy}
          prefillDriver={{ name: "", mobile: "" }}
          onClose={() => setNewVehicle(null)}
          onCreated={() => { setNewVehicle(null); toast.success("Request submitted to your Venson team."); reload(); }}
        />
      )}
    </FleetShell>
  );
}

// Resilient load-failure screen: refreshes the session on retry, and always
// offers a re-auth path so an expired/dead session can never trap the user.
function FleetLoadError({ onRetry }) {
  const nav = useNavigate();
  const { signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const tryAgain = async () => {
    setBusy(true);
    try { await supabase.auth.refreshSession(); } catch { /* ignore */ }
    onRetry();
    setTimeout(() => setBusy(false), 1500);
  };
  const signInAgain = async () => {
    try { await signOut(); } catch { /* ignore */ }
    nav("/login");
  };
  return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="fm-load-error">
      <div className="text-center max-w-sm">
        <p className="text-sm text-foreground mb-1">We couldn't load your fleet workspace.</p>
        <p className="text-xs text-muted-foreground mb-5">This is usually a brief connection issue or an expired session.</p>
        <div className="flex items-center justify-center gap-2">
          <Btn data-testid="fm-retry" onClick={tryAgain} loading={busy}>Try again</Btn>
          <Btn variant="outline" data-testid="fm-reauth" onClick={signInAgain}>Sign in again</Btn>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared presentational helpers
// ---------------------------------------------------------------------------
function PageHead({ eyebrow, title, count }) {
  return (
    <div className="mb-5">
      <p className="vda-tick text-[hsl(var(--vda-quiet))]">{eyebrow}</p>
      <h1 className="text-[28px] font-semibold tracking-tight text-foreground inline-flex items-center gap-3">
        {title}
        {count !== undefined && <span className="font-mono text-lg font-semibold tabular-nums text-muted-foreground">{count}</span>}
      </h1>
    </div>
  );
}

function Row({ left, right, onClick, testId }) {
  return (
    <div role="button" tabIndex={0} data-testid={testId} onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick && onClick(); } }}
      className="grid grid-cols-[1fr_auto] gap-3 items-center px-5 py-3 border-b border-border last:border-b-0 cursor-pointer hover:bg-secondary/40 focus:outline-none focus:bg-secondary/40 select-none">
      <div className="min-w-0">{left}</div>
      <div className="text-right">{right}</div>
    </div>
  );
}

const BucketTag = ({ days }) => {
  if (days === undefined || days === null) return null;
  if (days < 0) return <Tag tone="red">Overdue {Math.abs(days)}d</Tag>;
  if (days <= 7) return <Tag tone="red">{days}d</Tag>;
  if (days <= 30) return <Tag tone="amber">{days}d</Tag>;
  return <Tag tone="muted">{days}d</Tag>;
};

function flattenBuckets(mod, horizons, maxH) {
  const keys = ["now", ...horizons.map(String)];
  const items = [];
  for (const k of keys) {
    if (maxH !== null && k !== "now" && Number(k) > maxH) continue;
    (mod?.[k] || []).forEach((it) => items.push({ ...it, bucket: k }));
  }
  return items.sort((a, b) => (a.days ?? 999) - (b.days ?? 999));
}
const bucketTotal = (mod, horizons) => flattenBuckets(mod, horizons, null).length;

function HorizonTabs({ horizons, value, onChange }) {
  const tabs = useMemo(() => [
    { key: "now", label: "Now", max: 0 },
    ...horizons.map((h, i) => ({ key: String(h), label: i === horizons.length - 1 ? `\u2264${h} days` : `Next ${h}d`, max: h })),
    { key: "all", label: "All", max: null },
  ], [horizons]);
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="fd-horizons">
      <span className="vda-tick text-[hsl(var(--vda-quiet))] mr-1">Look ahead</span>
      {tabs.map((t) => {
        const on = (t.key === "all" && value === null) || (t.key !== "all" && value === t.max);
        return (
          <button key={t.key} data-testid={`fd-horizon-${t.key}`} onClick={() => onChange(t.key === "all" ? null : t.max)}
            className={`h-9 px-3.5 text-sm rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary text-foreground"}`}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function DomainPanel({ title, count, children }) {
  return (
    <Panel className="p-0 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold">{title}</h2>
        {count !== undefined && <span className="font-mono text-sm font-semibold tabular-nums text-muted-foreground">{count}</span>}
      </div>
      <div>{children}</div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// DASHBOARD — concise overview + deep-link summary cards
// ---------------------------------------------------------------------------
const CARD_META = {
  forward_service_mot: { label: "Service & MOT coming due", icon: Wrench, to: "/fleet/service-mot" },
  compliance_expiry: { label: "Compliance expiring", icon: FileClock, to: "/fleet/compliance" },
  open_recalls: { label: "Open safety recalls", icon: ShieldAlert, to: "/fleet/recalls" },
  contract_ending: { label: "Contracts ending", icon: CalendarClock, to: "/fleet/contracts" },
  weekly_checks: { label: "Weekly vehicle checks", icon: ClipboardCheck, to: "/fleet/checks" },
  open_requests: { label: "Open requests", icon: Inbox, to: "/fleet/requests" },
};

export function FleetDashboardPage() {
  const { dash, org, openVehicle } = useOutletContext();
  const nav = useNavigate();
  const { config, modules } = dash;
  const horizons = config?.horizons || [7, 30, 90];
  const enabled = config?.modules || Object.keys(CARD_META);
  const isEnabled = (k) => enabled.includes(k);

  const attention = (modules.attention_now || []).slice(0, 5);
  const urgentCount = (modules.urgent || []).filter((u) =>
    (u.kind === "compliance" && isEnabled("compliance_expiry")) ||
    ((u.kind === "service" || u.kind === "mot") && isEnabled("forward_service_mot"))).length;

  const cardValue = (k) => {
    if (k === "forward_service_mot") return { big: bucketTotal(modules.forward_service_mot, horizons), sub: "due \u226490 days" };
    if (k === "compliance_expiry") return { big: bucketTotal(modules.compliance_expiry, horizons), sub: "expiring soon" };
    if (k === "open_recalls") return { big: modules.open_recalls.length, sub: "vehicles affected" };
    if (k === "contract_ending") return { big: bucketTotal(modules.contract_ending, horizons), sub: "ending \u226490 days" };
    if (k === "weekly_checks") return { big: modules.weekly_checks.due_count, sub: `${modules.weekly_checks.with_defects.length} with defects` };
    if (k === "open_requests") return { big: modules.open_requests.count, sub: "in progress" };
    return { big: 0, sub: "" };
  };
  const cards = Object.keys(CARD_META).filter((k) => k === "open_requests" || isEnabled(k));

  return (
    <div data-testid="fm-view-dashboard">
      <PageHead eyebrow="Client workspace" title={org} />

      {/* Headline stats */}
      <div className="overflow-x-auto border-y border-[hsl(var(--vda-strong-border))] bg-card rounded-[8px]" data-testid="fd-summary">
        <div className="flex min-w-max">
          {[
            ["Active vehicles", modules.counts.active_vehicles, "green"],
            ["Open requests", modules.counts.open_bookings, "green"],
            ["Attention now", modules.counts.attention, modules.counts.attention > 0 ? "red" : "muted"],
          ].map(([label, value, tone]) => (
            <div key={label} className="flex flex-col justify-center gap-1.5 min-h-[74px] px-5 border-r border-border last:border-r-0" data-testid={`fd-stat-${String(label).toLowerCase().replace(/ /g, "-")}`}>
              <span className="vda-tick text-[hsl(var(--vda-quiet))] whitespace-nowrap">{label}</span>
              <span className={`font-mono text-[22px] leading-none font-semibold tabular-nums ${tone === "red" ? "text-[hsl(var(--vda-red))]" : tone === "muted" ? "text-foreground" : "text-primary"}`}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Operational summary cards — moved to the top so the fleet snapshot is visible without scrolling */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="fd-cards">
        {urgentCount > 0 && (
          <button data-testid="fd-card-urgent" onClick={() => nav("/fleet/urgent")}
            className="text-left rounded-[10px] border border-[hsl(var(--vda-red))]/40 bg-[hsl(var(--vda-red))]/8 p-4 hover:border-[hsl(var(--vda-red))]/60 hover:shadow-sm transition-all group">
            <div className="flex items-start justify-between">
              <span className="grid place-items-center h-9 w-9 rounded-[8px] bg-[hsl(var(--vda-red))]/12 text-[hsl(var(--vda-red))]"><AlertTriangle className="h-[18px] w-[18px]" strokeWidth={1.8} /></span>
              <ChevronRight className="h-4 w-4 text-[hsl(var(--vda-red))]/70 group-hover:text-[hsl(var(--vda-red))] transition-colors" />
            </div>
            <div className="mt-3 font-mono text-[26px] leading-none font-semibold tabular-nums text-[hsl(var(--vda-red))]">{urgentCount}</div>
            <div className="mt-1.5 text-sm font-medium text-foreground">Urgent — action needed now</div>
            <div className="text-xs text-muted-foreground">overdue or imminent</div>
          </button>
        )}
        {cards.map((k) => {
          const meta = CARD_META[k];
          const Icon = meta.icon;
          const { big, sub } = cardValue(k);
          const alert = (k === "open_recalls" && big > 0) || (k === "weekly_checks" && modules.weekly_checks.with_defects.length > 0);
          return (
            <button key={k} data-testid={`fd-card-${k}`} onClick={() => nav(meta.to)}
              className="text-left rounded-[10px] border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition-all group">
              <div className="flex items-start justify-between">
                <span className={`grid place-items-center h-9 w-9 rounded-[8px] ${alert ? "bg-[hsl(var(--vda-red))]/10 text-[hsl(var(--vda-red))]" : "bg-secondary text-muted-foreground"}`}>
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <div className="mt-3 font-mono text-[26px] leading-none font-semibold tabular-nums text-foreground">{big}</div>
              <div className="mt-1.5 text-sm font-medium text-foreground">{meta.label}</div>
              <div className="text-xs text-muted-foreground">{sub}</div>
            </button>
          );
        })}
      </div>

      {/* Attention now — concise, links into requests */}
      <div className="mt-6">
        <DomainPanel title="Needs attention now" count={modules.counts.attention}>
          {attention.length === 0 ? <EmptyState title="Nothing needs immediate attention" /> : (
            <>
              {attention.map((b) => (
                <Row key={b.id} testId={`fd-attention-${b.human_reference}`} onClick={() => openVehicle(b.registration)}
                  left={<><span className="vda-reg text-sm font-bold text-foreground">{b.registration ? formatReg(b.registration) : "Vehicle pending"}</span>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{REQUEST_LABEL[b.request_type] || b.request_type} · {b.human_reference}</div></>}
                  right={<><Tag tone={b.tone}>{b.note}</Tag><div className="font-mono text-[10px] text-[hsl(var(--vda-quiet))] mt-1">{b.appointment_at ? fmtDate(b.appointment_at) : "No date yet"}</div></>} />
              ))}
              <button data-testid="fd-attention-viewall" onClick={() => nav("/fleet/requests")}
                className="w-full px-5 py-3 text-sm font-semibold text-primary hover:bg-secondary/40 flex items-center justify-center gap-1">
                View all requests <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}
        </DomainPanel>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// URGENT — cross-module attention layer (own nav destination)
// ---------------------------------------------------------------------------
export function FleetUrgentPage() {
  const { dash, openVehicle } = useOutletContext();
  const { config, modules } = dash;
  const enabled = config?.modules || [];
  const isEnabled = (k) => enabled.includes(k);
  const urgent = (modules.urgent || []).filter((u) =>
    (u.kind === "compliance" && isEnabled("compliance_expiry")) ||
    ((u.kind === "service" || u.kind === "mot") && isEnabled("forward_service_mot")));
  return (
    <div data-testid="fm-view-urgent">
      <PageHead eyebrow="Attention" title="Urgent — action needed now" count={urgent.length} />
      <DomainPanel title="Overdue & imminent" count={urgent.length}>
        {urgent.length === 0 ? <EmptyState title="Nothing urgent right now" subtitle="Overdue and imminent service, MOT and compliance items appear here." /> : urgent.map((u, i) => (
          <Row key={i} testId={`fd-urgent-${i}`} onClick={() => openVehicle(u.registration)}
            left={<><span className="vda-reg text-sm font-bold text-foreground">{u.registration ? formatReg(u.registration) : "Fleet-wide"}</span>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">{u.label}</div></>}
            right={<Tag tone={u.state === "overdue" ? "red" : "amber"}>{u.state === "overdue" ? `Overdue ${Math.abs(u.days)}d` : `Due in ${u.days}d`}</Tag>} />
        ))}
      </DomainPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VEHICLES
// ---------------------------------------------------------------------------
export function FleetVehiclesPage() {
  const { activeVehicles } = useOutletContext();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [vehFilter, setVehFilter] = useState(null);

  let list = vehFilter ? activeVehicles.filter(FLEET_ACTIONS.find((a) => a.key === vehFilter).test) : activeVehicles;
  const s = q.trim().toLowerCase();
  if (s) list = list.filter((v) => `${v.registration} ${v.make} ${v.model}`.toLowerCase().includes(s));

  return (
    <div data-testid="fm-view-vehicles">
      <PageHead eyebrow="Fleet" title="Vehicles" count={activeVehicles.length} />
      <Panel className="p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="veh-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Registration or make/model" className="pl-9" />
        </div>
      </Panel>
      <div className="flex flex-wrap items-center gap-2 mt-4 mb-3" data-testid="veh-filters">
        {FLEET_ACTIONS.map((a) => {
          const on = vehFilter === a.key;
          return (
            <button key={a.key} data-testid={`veh-filter-${a.key}`} onClick={() => setVehFilter(on ? null : a.key)}
              className={`h-9 px-3.5 text-sm rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary text-foreground"}`}>
              {a.label}
            </button>
          );
        })}
      </div>
      <VehicleMileageTable vehicles={list} onOpen={(vid) => nav(`/fleet/vehicle/${vid}`)}
        filterLabel={vehFilter ? FLEET_ACTIONS.find((a) => a.key === vehFilter).label : null}
        onClearFilter={() => setVehFilter(null)} />
    </div>
  );
}

function VehicleMileageTable({ vehicles, onOpen, filterLabel, onClearFilter }) {
  if (vehicles.length === 0) return <EmptyState title={filterLabel ? `No vehicles: ${filterLabel}` : "No vehicles"} />;
  return (
    <Panel className="overflow-hidden">
      {filterLabel && (
        <div className="px-4 py-2 flex items-center justify-between border-b border-border bg-secondary/40 text-xs">
          <span>Filtered: <strong>{filterLabel}</strong></span>
          <button data-testid="ext-clear-filter" onClick={onClearFilter} className="text-primary font-semibold hover:underline">Clear</button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px] select-none">
          <thead>
            <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
              <th className="px-3 py-2 font-medium">Registration</th>
              <th className="px-3 py-2 font-medium">Vehicle</th>
              <th className="px-3 py-2 font-medium">MOT due</th>
              <th className="px-3 py-2 font-medium">Service due</th>
              <th className="px-3 py-2 font-medium">Mileage</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id} role="button" tabIndex={0}
                className="border-t border-border cursor-pointer hover:bg-secondary/40 select-none"
                data-testid={`ext-vehicle-${formatReg(v.registration)}`}
                onClick={() => onOpen(v.id)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(v.id); }}>
                <td className="px-3 py-2 vda-reg font-bold text-primary">{formatReg(v.registration)}</td>
                <td className="px-3 py-2">{v.make} {v.model}</td>
                <td className="px-3 py-2">{fmtDate(v.mot_due_date)}</td>
                <td className="px-3 py-2">{fmtDate(v.service_due_date)}</td>
                <td className="px-3 py-2 font-mono tabular-nums">{v.current_mileage?.toLocaleString() || "—"}</td>
                <td className="px-3 py-2">{v.open_recall ? <Tag tone="red">Recall</Tag> : <Tag tone="muted">{v.vehicle_status}</Tag>}</td>
                <td className="px-3 py-2 text-right"><span className="text-xs font-semibold text-primary">Open profile →</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// REQUESTS
// ---------------------------------------------------------------------------
export function FleetRequestsPage() {
  const { bookings, openVehicle } = useOutletContext();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [tab, setTab] = useState("active");

  const filtered = useMemo(() => {
    let b = bookings;
    if (q) b = b.filter((x) => `${x.registration} ${x.driver_name} ${x.human_reference}`.toLowerCase().includes(q.toLowerCase()));
    if (status) b = b.filter((x) => x.public_status === status);
    if (type) b = b.filter((x) => x.request_type === type);
    return b;
  }, [bookings, q, status, type]);

  const active = filtered.filter((b) => b.public_status !== "Completed");
  const completed = filtered.filter((b) => b.public_status === "Completed");
  const publicStatuses = [...new Set(bookings.map((b) => b.public_status))];

  return (
    <div data-testid="fm-view-requests">
      <PageHead eyebrow="Bookings" title="Requests" count={bookings.length} />
      <Panel className="p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="ext-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Registration, driver or reference" className="pl-9" />
        </div>
        <Select data-testid="ext-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[160px]">
          <option value="">All statuses</option>
          {publicStatuses.map((st) => <option key={st} value={st}>{st}</option>)}
        </Select>
        <Select data-testid="ext-type" value={type} onChange={(e) => setType(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">All types</option>
          {REQUEST_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select>
      </Panel>
      <div className="grid grid-cols-2 gap-2 mt-5 mb-3 max-w-md">
        {[["active", "Active", active.length], ["completed", "Completed", completed.length]].map(([k, l, c]) => (
          <button key={k} data-testid={`ext-tab-${k}`} onClick={() => setTab(k)}
            className={`min-h-[44px] px-2 py-1.5 text-sm rounded-[8px] border flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors ${tab === k ? "bg-primary text-primary-foreground border-primary" : "border-[hsl(var(--input))] bg-card hover:bg-secondary"}`}>
            <span className="truncate">{l}</span>
            <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1 rounded-full text-xs font-semibold tabular-nums ${tab === k ? "bg-primary-foreground/20" : "bg-secondary"}`}>{c}</span>
          </button>
        ))}
      </div>
      <BookingTable rows={tab === "active" ? active : completed} onOpenVehicle={openVehicle} />
    </div>
  );
}

function BookingTable({ rows, onOpenVehicle }) {
  if (rows.length === 0) return <EmptyState title="No bookings in this view" />;
  return (
    <Panel className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
              <th className="px-3 py-2 font-medium">Reference</th>
              <th className="px-3 py-2 font-medium">Registration</th>
              <th className="px-3 py-2 font-medium">Driver</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Appointment</th>
              <th className="px-3 py-2 font-medium">Garage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} role="button" tabIndex={0}
                className="border-t border-border cursor-pointer hover:bg-secondary/40 focus:outline-none focus:bg-secondary/40 select-none"
                data-testid={`ext-booking-${b.human_reference}`}
                onClick={() => onOpenVehicle && onOpenVehicle(b.registration)}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onOpenVehicle) { e.preventDefault(); onOpenVehicle(b.registration); } }}>
                <td className="px-3 py-2 vda-reg">{b.human_reference}</td>
                <td className="px-3 py-2 vda-reg font-bold text-primary">{formatReg(b.registration)}</td>
                <td className="px-3 py-2">{b.driver_name}</td>
                <td className="px-3 py-2">{REQUEST_LABEL[b.request_type] || b.request_type}{b.booking_type ? <span className="block text-[11px] text-muted-foreground">{b.booking_type}</span> : null}</td>
                <td className="px-3 py-2"><Tag tone={b.public_status === "Completed" ? "muted" : "green"}>{b.public_status}</Tag></td>
                <td className="px-3 py-2">{b.appointment_at ? fmtDate(b.appointment_at) : "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{b.garage_name || "—"}{b.garage_phone ? <span className="block text-[11px] font-mono">{b.garage_phone}</span> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// WEEKLY CHECKS
// ---------------------------------------------------------------------------
export function FleetChecksPage() {
  const { dash, openVehicle } = useOutletContext();
  const wc = dash.modules.weekly_checks;
  return (
    <div data-testid="fm-view-checks">
      <PageHead eyebrow="Fleet safety" title="Weekly Checks" />
      <div className="grid sm:grid-cols-4 gap-4 mb-5" data-testid="fd-weekly-summary">
        {[["Checked this week", wc.checked_this_week], ["Fleet size", wc.fleet_size], ["Due", wc.due_count], ["With defects", wc.with_defects.length]].map(([l, v]) => (
          <Panel key={l} className="p-4">
            <div className="vda-tick text-[hsl(var(--vda-quiet))]">{l}</div>
            <div className="font-mono text-2xl font-semibold tabular-nums mt-1">{v}</div>
          </Panel>
        ))}
      </div>
      <DomainPanel title="Checks with defects" count={wc.with_defects.length}>
        {wc.with_defects.length === 0 ? <EmptyState title="No outstanding check defects" /> : wc.with_defects.map((d) => (
          <Row key={d.id} testId={`fd-checkdefect-${formatReg(d.registration)}`} onClick={() => openVehicle(d.registration)}
            left={<><span className="vda-reg text-sm font-bold text-foreground">{formatReg(d.registration)}</span>
              <div className="text-xs text-muted-foreground mt-0.5">{d.submitted_at ? `Submitted ${fmtDate(d.submitted_at)}` : "Submitted"}</div></>}
            right={<Tag tone="amber">{d.defect_count} defect{d.defect_count === 1 ? "" : "s"}</Tag>} />
        ))}
      </DomainPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DATE-DRIVEN DOMAINS (Service & MOT, Compliance, Contracts) — share horizon filter
// ---------------------------------------------------------------------------
function HorizonDomainView({ testId, eyebrow, title, mod, render, meta, rowTestId }) {
  const { dash, openVehicle } = useOutletContext();
  const horizons = dash.config?.horizons || [7, 30, 90];
  const [horizon, setHorizon] = useState(null);
  const items = flattenBuckets(mod, horizons, horizon);
  return (
    <div data-testid={testId}>
      <PageHead eyebrow={eyebrow} title={title} count={bucketTotal(mod, horizons)} />
      <HorizonTabs horizons={horizons} value={horizon} onChange={setHorizon} />
      <DomainPanel title={title} count={items.length}>
        {items.length === 0 ? <EmptyState title="Nothing in this window" /> : items.map((it, i) => (
          <Row key={(it.vehicle_id || it.id || i) + it.bucket + (it.kind || it.record_type || i)}
            testId={`${rowTestId}-${i}`} onClick={() => openVehicle(it.registration)}
            left={render(it)} right={<><BucketTag days={it.days} /><div className="font-mono text-[10px] text-[hsl(var(--vda-quiet))] mt-1">{meta(it)}</div></>} />
        ))}
      </DomainPanel>
    </div>
  );
}

export function FleetServiceMotPage() {
  const { dash } = useOutletContext();
  return (
    <HorizonDomainView testId="fm-view-service-mot" eyebrow="Maintenance" title="Service & MOT"
      mod={dash.modules.forward_service_mot} rowTestId="fd-service-mot"
      render={(it) => (<><span className="vda-reg text-sm font-bold text-foreground">{formatReg(it.registration)}</span>
        <span className="ml-2 text-xs text-muted-foreground uppercase">{it.kind}</span></>)}
      meta={(it) => <>{fmtDate(it.due_date)}</>} />
  );
}

export function FleetCompliancePage() {
  const { dash } = useOutletContext();
  return (
    <HorizonDomainView testId="fm-view-compliance" eyebrow="Compliance" title="Compliance"
      mod={dash.modules.compliance_expiry} rowTestId="fd-compliance"
      render={(it) => (<><span className="vda-reg text-sm font-bold text-foreground">{it.registration ? formatReg(it.registration) : "Fleet-wide"}</span>
        <div className="text-xs text-muted-foreground mt-0.5">{it.title || it.record_type}</div></>)}
      meta={(it) => <>{fmtDate(it.expiry_date)}</>} />
  );
}

export function FleetContractsPage() {
  const { dash } = useOutletContext();
  return (
    <HorizonDomainView testId="fm-view-contracts" eyebrow="Contracts" title="Contracts"
      mod={dash.modules.contract_ending} rowTestId="fd-contract"
      render={(it) => <span className="vda-reg text-sm font-bold text-foreground">{formatReg(it.registration)}</span>}
      meta={(it) => <>{fmtDate(it.end_date)}</>} />
  );
}

// ---------------------------------------------------------------------------
// RECALLS
// ---------------------------------------------------------------------------
export function FleetRecallsPage() {
  const { dash, openVehicle } = useOutletContext();
  const recalls = dash.modules.open_recalls;
  return (
    <div data-testid="fm-view-recalls">
      <PageHead eyebrow="Safety" title="Recalls" count={recalls.length} />
      <DomainPanel title="Open safety recalls" count={recalls.length}>
        {recalls.length === 0 ? <EmptyState title="No open recalls" /> : recalls.map((r) => (
          <Row key={r.vehicle_id} testId={`fd-recall-${formatReg(r.registration)}`} onClick={() => openVehicle(r.registration)}
            left={<><span className="vda-reg text-sm font-bold text-foreground">{formatReg(r.registration)}</span>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">{r.summary || "Open safety recall"}</div></>}
            right={<Tag tone="red">Recall</Tag>} />
        ))}
      </DomainPanel>
    </div>
  );
}
